import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Grid,
  Button,
  TableFooter
} from '@mui/material';
import { API_BASE_URL } from 'src/apiConfig';
import GradeSolutionEditorDialog from './GradeSolutionEditorDialog';
import { useTranslation } from 'react-i18next';
import { calculatePoints } from '../reviewProgress/calculatePoints';
import { isAdmin } from 'src/services/authService';

// prikaz tabele i rada selektovanog učenika iz komponente classStudent.
const StudentDetails = ({ studentId, onUpdate }) => {
  const { t } = useTranslation(); // Додатак за превод
  const [studentData, setStudentData] = useState(null);
  const [calculatedPoints, setCalculatedPoints] = useState({
    totalPoints: 0,
    detailedResults: []
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [corectSolutions, setCorectSolutions] = useState('');

  const fetchSolutions = async (gradeClass) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/finalGradeSolutions.php?grade=${gradeClass}`
      );
      setCorectSolutions(response.data);
      return response.data;
    } catch (error) {
      console.error(t('solutions.errorFetchingSolutions'), error);
      return '';
    }
  };

  const fetchStudentData = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/finalReviewAll.php?action=getStudentInfoFinalResult&idStudent=${studentId}`
      );

      setStudentData(response.data);
      fetchSolutions(response.data.class).then((correctSolutions) => {
        calculateAndSetPoints(response.data.result, correctSolutions);
      });
    } catch (error) {
      console.error('Error fetching student data:', error);
    }
  };

  const calculateAndSetPoints = (newResult, correctSolutions) => {
    const { totalPoints, detailedResults } = calculatePoints(
      correctSolutions,
      newResult,
      true
    );
    setCalculatedPoints({ totalPoints, detailedResults });
  };

  useEffect(() => {
    fetchStudentData();
  }, [studentId]);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleSave = () => {
    setDialogOpen(false);
    fetchStudentData(); // Osveži trenutnog učenika

    if (onUpdate) {
      onUpdate(); // Obavesti roditelja o promeni (poziva `fetchStudents` u roditelju)
    }
  };

  if (!studentData) return <div>{t('review.loading')}</div>;

  return (
    <Container
      style={{
        backgroundColor: '#f8f9fa',
        padding: '10px',
        paddingTop: '2px',
        borderRadius: '8px',
        maxWidth: '100%',
        margin: 'auto'
      }}
    >
      {dialogOpen && (
        <GradeSolutionEditorDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          grade={studentData.class} // Prosleđuješ razred
          solutionString={studentData.result} // Prosleđuješ trenutne odgovore učenika
          pointsString={studentData.poeni} // Prosleđuješ trenutne poene učenika
          studentId={studentData.id} // Prosleđuješ studentId
          fetchSolutions={fetchSolutions} // Prosleđuješ funkciju za dohvatanje tačnih rešenja
          onSave={handleSave} // Pozivaš osvežavanje podataka nakon što se sve sačuva
        />
      )}
      <Grid item xs={12}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          style={{ marginBottom: '20px' }}
        >
          <Typography
            variant="h4"
            component="div"
            sx={{
              fontWeight: 'bold',
              color: '#1976d2'
            }}
          >
            {studentData.name}
          </Typography>
          {isAdmin() && (
            <Button
              variant="outlined"
              size="small"
              style={{
                borderColor: '#1976D2',
                color: '#1976D2',
                borderRadius: '8px',
                padding: '6px 12px',
                fontSize: '12px',
                textTransform: 'none'
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = '#e3f2fd')
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = 'transparent')
              }
              onClick={handleDialogOpen}
            >
              {t('review.change')}
            </Button>
          )}
        </Box>
        <TableContainer
          component={Paper}
          style={{
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
            marginTop: '20px'
          }}
        >
          <Table aria-label="student details table">
            <TableHead style={{ backgroundColor: '#1976D2' }}>
              <TableRow>
                <TableCell
                  style={{
                    color: '#ffffff',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    fontSize: '12px',
                    padding: '4px'
                  }}
                >
                  {t('review.number')}
                </TableCell>
                {['A', 'B', 'V', 'G', 'D'].map((letter) => (
                  <TableCell
                    key={letter}
                    style={{
                      color: '#ffffff',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      fontSize: '12px',
                      padding: '4px'
                    }}
                  >
                    {letter}
                  </TableCell>
                ))}
                <TableCell
                  style={{
                    color: '#ffffff',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    fontSize: '12px',
                    padding: '4px',
                    width: '80px'
                  }}
                >
                  {t('review.points')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calculatedPoints.detailedResults.map(
                (
                  { questionNumber, correctAnswer, studentAnswers, points },
                  index
                ) => {
                  const letters = ['A', 'B', 'V', 'G', 'D'];

                  return (
                    <TableRow
                      key={index}
                      style={{
                        backgroundColor:
                          index % 2 === 0 ? '#f1f3f5' : '#ffffff',
                        transition: 'background-color 0.3s ease'
                      }}
                    >
                      <TableCell
                        style={{
                          fontWeight: 'bold',
                          textAlign: 'center',
                          padding: '4px'
                        }}
                      >
                        {questionNumber}
                      </TableCell>
                      {letters.map((letter) => {
                        const isCorrect = correctAnswer === letter;
                        const isStudentAnswer = studentAnswers.includes(letter);
                        let backgroundColor = '#f8f9fa';

                        if (isCorrect) {
                          backgroundColor = '#d1e7dd';
                        }
                        if (isStudentAnswer && !isCorrect) {
                          backgroundColor = '#f8d7da';
                        }

                        return (
                          <TableCell
                            key={letter}
                            style={{
                              backgroundColor: backgroundColor,
                              textAlign: 'center',
                              padding: '4px',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            {isStudentAnswer ? 'X' : ''}
                          </TableCell>
                        );
                      })}
                      <TableCell
                        style={{
                          textAlign: 'center',
                          backgroundColor: '#ffffff',
                          fontWeight: 'bold',
                          padding: '4px',
                          color: points < 0 ? '#ff6b6b' : '#28a745',
                          width: '80px',
                          border: '1px solid #dee2e6'
                        }}
                      >
                        {points < 0 ? points.toFixed(2) : points}
                      </TableCell>
                    </TableRow>
                  );
                }
              )}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TableCell
                  colSpan={6}
                  style={{
                    textAlign: 'center',
                    padding: '8px',
                    backgroundColor: '#1976D2',
                    color: '#ffffff',
                    fontWeight: 'bold'
                  }}
                >
                  {t('review.total')}
                </TableCell>
                <TableCell
                  style={{
                    textAlign: 'center',
                    padding: '8px',
                    backgroundColor: '#1976D2',
                    color: '#ffffff',
                    fontWeight: 'bold'
                  }}
                >
                  {calculatedPoints.totalPoints}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Grid>
    </Container>
  );
};

export default StudentDetails;
